import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { boltAltIcon, scoreIcon, signupHero } from '@/assets/images/auth';
import styles from './SignUpPage.module.scss';
import { LottieAnimation } from '@/components/LottieAnimation';
import { Button } from '@/ui/components/Button';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  isValidPhoneNumber,
  normalizePhoneNumber,
  normalizePhoneInput,
} from '@/lib/phoneUtils';
import { routes } from '@/routes/routes';
import { externalUrls } from '@/constants/urls';
import { useError } from '@/providers/Error';
import { genericMessages, phoneOtpMessages } from '@/constants/errorMessages';
import { Icon } from '@/ui/icons/Icon';

const loadVoltCoins = () =>
  import('@/assets/animations/volt-web-coins.json').then(
    (module) => module.default,
  );

function SignUpPage() {
  const { requestOtp, isRequestingOtp, setAuthPhoneNumber, requestOtpError } =
    useAuth();
  const navigate = useNavigate();
  const { setError, error } = useError();
  const [searchParams] = useSearchParams();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [isHeroStill, setIsHeroStill] = useState(false);
  const isPasting = useRef(false);

  const MAX_PHONE_LENGTH = 9; // max local digits: 7XXXXXXXX

  // Prefill the phone field from a `?phone=` query param when it is a valid
  // Romanian number (e.g. arriving from a campaign or broker-offer link).
  const phonePrefill = searchParams.get('phone');
  useEffect(() => {
    if (!phonePrefill) return;
    const normalized = normalizePhoneNumber(phonePrefill);
    if (isValidPhoneNumber(normalized)) {
      setPhoneNumber(normalizePhoneInput(normalized));
    }
  }, [phonePrefill]);

  useEffect(() => {
    if (requestOtpError) {
      setError(genericMessages.unexpected);
    }
  }, [requestOtpError]);

  const handleSubmit = async () => {
    if (!phoneNumber.trim()) return;

    const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
    if (!isValidPhoneNumber(normalizedPhoneNumber)) {
      setError(phoneOtpMessages.invalid_phone_number);
      return;
    }

    const response = await requestOtp({ phone: normalizedPhoneNumber });

    if (!response.isSuccess) {
      setError(
        phoneOtpMessages[response.error as keyof typeof phoneOtpMessages],
      );
      return;
    }

    setAuthPhoneNumber(normalizedPhoneNumber);
    navigate(routes.public.phoneConfirmation);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const processedValue = normalizePhoneInput(e.target.value);
    if (!isPasting.current && processedValue.length > MAX_PHONE_LENGTH) return;
    isPasting.current = false;
    setPhoneNumber(processedValue);
  };

  const handlePaste = () => {
    isPasting.current = true;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.hero}>
            {/* For users asking for less motion, or if it could not play. */}
            {isHeroStill ? (
              <img src={signupHero} alt="" width={217} height={128} />
            ) : (
              <LottieAnimation
                load={loadVoltCoins}
                loopMarker="loop"
                className={styles.heroAnimation}
                onComplete={() => setIsHeroStill(true)}
              />
            )}
          </div>
          <h1 className={styles.title}>
            Află oferta ta
            <br />
            de credit cu Volt
          </h1>
          <p className={styles.subtitle}>
            Ofertă personalizată în mai puțin de 1 minut.
          </p>
        </div>

        <div className={styles.phoneSection}>
          <div
            className={clsx(styles.phone, {
              [styles['phone--error']]: !!error,
            })}
          >
            <span className={styles.countryCode}>+40</span>
            <input
              type="tel"
              placeholder="Introdu numărul de telefon"
              aria-label="Număr de telefon"
              value={phoneNumber}
              onChange={handlePhoneChange}
              onKeyDown={handleKeyDown}
              className={styles.phoneInput}
              autoComplete="tel"
              autoFocus={true}
              inputMode="numeric"
              enterKeyHint="done"
              onPaste={handlePaste}
            />
          </div>
        </div>

        <div className={styles.usps}>
          <div className={styles.usp}>
            <img src={scoreIcon} alt="" width={32} height={32} />
            <p className={styles.uspText}>
              Fără impact
              <br />
              asupra scorului
            </p>
          </div>
          <div className={styles.usp}>
            <img src={boltAltIcon} alt="" width={32} height={32} />
            <p className={styles.uspText}>
              Ofertă
              <br />
              personalizată
            </p>
          </div>
        </div>
      </div>

      <div className={styles.bottom}>
        <Button
          trackingId="continue_with_phone"
          variant="filled"
          text="Continuă"
          className={styles.button}
          onClick={handleSubmit}
          loading={isRequestingOtp}
          rightIcon={<Icon name="arrow-right" />}
        />
        <p className={styles.terms}>
          Prin continuare, accept{' '}
          <Link
            to={externalUrls.volt.terms}
            target="_blank"
            className={styles.link}
          >
            Termenii
          </Link>{' '}
          și confirm <br /> că am citit{' '}
          <Link
            to={externalUrls.volt.privacy}
            target="_blank"
            className={styles.link}
          >
            Politica de confidențialitate
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

export default SignUpPage;
